import { Accessor, batch, createSignal, Setter } from 'solid-js';
import {
  CapabilityRegistry,
  CapabilityRegistryContainer,
} from '@trimble/web3d';
import type { InteractionPane } from './interaction-pane.types';

interface PaneSignal {
  readonly get: Accessor<CapabilityRegistryContainer | undefined>;
  readonly set: Setter<CapabilityRegistryContainer | undefined>;
}

interface RegistryPanes {
  readonly interaction: PaneSignal;
  readonly lockedTool: PaneSignal;
}

const registryPanes = new WeakMap<CapabilityRegistry, RegistryPanes>();

function createPaneSignal(): PaneSignal {
  const [get, set] = createSignal<CapabilityRegistryContainer>();
  return { get, set };
}

function panesOf(capabilityRegistry: CapabilityRegistry): RegistryPanes {
  let panes = registryPanes.get(capabilityRegistry);
  if (!panes) {
    panes = { interaction: createPaneSignal(), lockedTool: createPaneSignal() };
    registryPanes.set(capabilityRegistry, panes);
  }
  return panes;
}

// The interaction pane keeps tools on while the pointer is over the toolbar or a side panel.
// A press in another pane always moves the tool pane there, discarding a locked pane.
export function trackInteractionPane(
  capabilityRegistry: CapabilityRegistry,
  container: CapabilityRegistryContainer
): InteractionPane {
  const { interaction, lockedTool } = panesOf(capabilityRegistry);
  const [presses, setPresses] = createSignal(0);

  const unlockToolPane = (): void => {
    if (lockedTool.get() === container) {
      lockedTool.set(undefined);
    }
  };

  return {
    isInteractionPane: () => interaction.get() === container,
    isToolPane: () => (lockedTool.get() ?? interaction.get()) === container,
    presses,
    claim: () => interaction.set(container),
    press: () => {
      batch(() => {
        interaction.set(container);
        if (lockedTool.get() !== container) {
          lockedTool.set(undefined);
        }
        setPresses((count) => count + 1);
      });
    },
    lockToolPane: () => lockedTool.set(container),
    unlockToolPane,
    release: () => {
      batch(() => {
        unlockToolPane();
        if (interaction.get() === container) {
          interaction.set(undefined);
        }
      });
    },
  };
}
